import React from 'react';
import { useHistory } from 'react-router-dom';
import Text from '@service-victoria/component-library/components/Text';
import Notification from '@service-victoria/component-library/components/Notification';
import List from '@service-victoria/component-library/components/List';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import { constants } from 'utils';
import * as Interfaces from 'interfaces';
import * as Styled from '../styled';
import { TextBodyWithInlineLink } from 'pages/Home/styled';
import { getStreetAddress } from './helpers';

const cardLabel = (type?: string) => {
  switch (type) {
    case 'licence':
      return 'Driver licence number: ';
    case 'seniorsCard':
      return 'Seniors card number: ';
    case 'proofOfAgeCard':
      return 'Proof of age card number: ';
    default:
      return '';
  }
};

export type ComponentProps = StepComponent<Partial<Interfaces.Common.FormValues>>;

const Review: React.FC<ComponentProps> = ({ context }) => {
  const { values: vRaw } = context.form;
  const values = vRaw as Interfaces.Common.FormValues;

  const history = useHistory();
  const { currentState } = context.appStates[
    constants.StorageKeys.State
  ] as Interfaces.Store.AppContext;
  const [errorCount, setErrorCount] = React.useState(0);
  const homeStreetAddress = getStreetAddress(values.identity.address?.fullDetails?.fullAddress);

  React.useEffect(() => {
    if (currentState.error) {
      setErrorCount(errorCount => errorCount + 1);
    }
  }, [currentState.error]);

  React.useEffect(() => {
    if (errorCount >= 3) {
      history.push(constants.Routes.TechnicalError);
    }
  }, [errorCount, history]);

  const displayDocumentDetails = () => {
    return (
      <React.Fragment>
        <Text.Body>
          Given name: <b>{values.identity.givenName}</b>
        </Text.Body>
        {values.identity.middleName && values.identity.proofType === 'licence' && (
          <Text.Body>
            Middle name: <b>{values.identity.middleName}</b>
          </Text.Body>
        )}
        <Text.Body>
          Family name: <b>{values.identity.familyName}</b>
        </Text.Body>
        <Text.Body>
          Date of birth: <b>{values.identity.dob}</b>
        </Text.Body>
        <Text.Body>
          {cardLabel(values.identity.proofType)} <b>{values.identity.cardNo}</b>
        </Text.Body>
      </React.Fragment>
    );
  };

  return (
    <Styled.Wrapper>
      <Text.Heading.H1 fontWeight={600}>Review</Text.Heading.H1>
      <Styled.Spacer16 />
      {!!currentState.error && (
        <Styled.Error>
          <Notification.Error.Text
            title={currentState.error?.title ?? ''}
            value={currentState.error?.value ?? ''}
          />
          <Styled.Spacer48 />
        </Styled.Error>
      )}
      <List
        type='disc'
        items={[
          'Carefully check all of the information below is correct',
          <TextBodyWithInlineLink key='1'>
            If anything is wrong or needs to be changed, simply{' '}
            <Text.Link
              target='_self'
              value='start over'
              href={constants.internalLinks.beforeYouStart}
            />
          </TextBodyWithInlineLink>
        ]}
      />
      <React.Fragment>
        <Text.Body>
          Your email: <b>{values.identity.email}</b>
        </Text.Body>
        <Text.Body>
          Phone: <b>{values.identity.phone}</b>
        </Text.Body>
        {displayDocumentDetails()}
        <Text.Body>
          Address:{' '}
          <b>
            {homeStreetAddress}, {values.identity.address?.fullDetails?.suburb},{' '}
            {values.identity.address?.fullDetails?.state},{' '}
            {values.identity.address?.fullDetails?.postCode}
          </b>
        </Text.Body>
      </React.Fragment>
    </Styled.Wrapper>
  );
};

export default Review;
